import React, { useCallback, useMemo } from "react";
import { createEditor, Descendant, BaseEditor, Editor, Text } from "slate";
import {
  Slate,
  Editable,
  withReact,
  RenderElementProps,
  RenderLeafProps,
  ReactEditor,
  useSlate,
} from "slate-react";
import { withHistory, HistoryEditor } from "slate-history";
import { Bold, Italic } from "lucide-react";
import TextComponent from "@core/text/index";
import { Element as SlateElement } from "slate";
import { Transforms } from "slate";

type CustomText = {
  text: string;
  size?: TextSize;
  bold?: boolean;
  italic?: boolean;
  underline?: boolean;
  align?: Alignment;
};

type CustomElement = {
  type: ElementType;
  align?: Alignment;
  children: Descendant[];
};

type ElementType =
  | "paragraph"
  | "heading-one"
  | "heading-two"
  | "heading-three"
  | "heading-four"
  | "heading-five"
  | "heading-six"
  | "bulleted-list"
  | "numbered-list"
  | "list-item"
  | "ai-content";

type TextSize = "sm" | "md" | "lg" | "xl";
type Alignment = "left" | "center" | "right" | "justify";

const initialValue: CustomElement[] = [
  {
    type: "paragraph",
    children: [{ text: "" }],
  },
];

// Extend the Slate types
declare module "slate" {
  interface CustomTypes {
    Editor: BaseEditor & ReactEditor & HistoryEditor;
    Element: CustomElement;
    Text: CustomText;
  }
}

// Deserialize HTML to Slate format
const deserialize = (html: string): CustomElement[] => {
  const parser = new DOMParser();
  const doc = parser.parseFromString(html, "text/html");
  const nodes: CustomElement[] = [];

  const parseTextNode = (node: Node): CustomText[] => {
    if (node.nodeType === Node.TEXT_NODE) {
      return [{ text: node.textContent || "" }];
    }

    if (node.nodeType === Node.ELEMENT_NODE) {
      const element = node as Element;
      const children: CustomText[] = [];

      // Get the text content and apply formatting based on tag name
      const textContent = element.textContent || "";

      if (textContent) {
        const textNode: CustomText = { text: textContent };

        // Apply formatting based on tag name
        if (element.tagName === "B" || element.tagName === "STRONG") {
          textNode.bold = true;
        }
        if (element.tagName === "I" || element.tagName === "EM") {
          textNode.italic = true;
        }

        children.push(textNode);
      }

      return children;
    }

    return [{ text: "" }];
  };

  const parseElementChildren = (element: Element): CustomText[] => {
    const children: CustomText[] = [];

    for (const child of Array.from(element.childNodes)) {
      if (child.nodeType === Node.TEXT_NODE) {
        const text = child.textContent || "";
        if (text.trim()) {
          children.push({ text });
        }
      } else if (child.nodeType === Node.ELEMENT_NODE) {
        const childElement = child as Element;
        const text = childElement.textContent || "";

        if (text.trim()) {
          const textNode: CustomText = { text };

          // Apply formatting based on tag name
          if (
            childElement.tagName === "B" ||
            childElement.tagName === "STRONG"
          ) {
            textNode.bold = true;
          }
          if (childElement.tagName === "I" || childElement.tagName === "EM") {
            textNode.italic = true;
          }

          children.push(textNode);
        }
      }
    }

    return children.length > 0 ? children : [{ text: "" }];
  };

  doc.body.childNodes.forEach((node) => {
    if (node.nodeType === Node.ELEMENT_NODE) {
      const element = node as Element;

      if (element.tagName === "P") {
        nodes.push({
          type: "paragraph",
          children: parseElementChildren(element),
        });
      } else if (element.tagName === "H1") {
        nodes.push({
          type: "heading-one",
          children: parseElementChildren(element),
        });
      } else if (element.tagName === "H2") {
        nodes.push({
          type: "heading-two",
          children: parseElementChildren(element),
        });
      } else if (element.tagName === "H3") {
        nodes.push({
          type: "heading-three",
          children: parseElementChildren(element),
        });
      } else if (element.tagName === "H4") {
        nodes.push({
          type: "heading-four",
          children: parseElementChildren(element),
        });
      } else if (element.tagName === "H5") {
        nodes.push({
          type: "heading-five",
          children: parseElementChildren(element),
        });
      } else if (element.tagName === "H6") {
        nodes.push({
          type: "heading-six",
          children: parseElementChildren(element),
        });
      } else if (element.tagName === "UL") {
        const listItems: ListItemElement[] = [];

        element.childNodes.forEach((li) => {
          if (
            li.nodeType === Node.ELEMENT_NODE &&
            (li as Element).tagName === "LI"
          ) {
            listItems.push({
              type: "list-item",
              children: parseElementChildren(li as Element),
            });
          }
        });

        if (listItems.length > 0) {
          nodes.push({
            type: "bulleted-list",
            children: listItems,
          });
        }
      } else if (
        element.tagName === "B" ||
        element.tagName === "STRONG" ||
        element.tagName === "I" ||
        element.tagName === "EM"
      ) {
        // Handle standalone formatting tags as paragraphs
        nodes.push({
          type: "paragraph",
          children: parseTextNode(element),
        });
      }
    } else if (node.nodeType === Node.TEXT_NODE) {
      const text = node.textContent || "";
      if (text.trim()) {
        nodes.push({
          type: "paragraph",
          children: [{ text: text.trim() }],
        });
      }
    }
  });

  return nodes.length > 0 ? nodes : initialValue;
};

// Serialize Slate format back to HTML
const serialize = (nodes: CustomElement[]): string => {
  return nodes
    .map((node) => {
      if (!("children" in node)) return "";

      if (node.type === "bulleted-list") {
        const listItems = (node.children as ListItemElement[])
          .map((item) => {
            if (item.type !== "list-item") return "";
            const itemContent = (item.children as CustomText[])
              .map((child) => {
                if (!Text.isText(child)) return "";
                let text = child.text.trim();
                if (!text) return "";

                if (child.bold) {
                  text = `<strong> ${text} </strong>`;
                }
                if (child.italic) {
                  text = `<em> ${text} </em>`;
                }
                return text;
              })
              .join("");
            return `<li>${itemContent}</li>`;
          })
          .join("");
        return `<ul>${listItems}</ul>`;
      }

      if (node.type === "numbered-list") {
        const listItems = (node.children as ListItemElement[])
          .map((item) => {
            if (item.type !== "list-item") return "";
            const itemContent = (item.children as CustomText[])
              .map((child) => {
                if (!Text.isText(child)) return "";
                let text = child.text.trim();
                if (!text) return "";

                if (child.bold) {
                  text = `<strong> ${text} </strong>`;
                }
                if (child.italic) {
                  text = `<em> ${text} </em>`;
                }
                return text;
              })
              .join("");
            return `<li>${itemContent}</li>`;
          })
          .join("");
        return `<ol>${listItems}</ol>`;
      }

      const children = (node.children as CustomText[])
        .map((child) => {
          if (!Text.isText(child)) return "";
          let text = child.text.trim();

          if (!text) return "";

          if (child.bold) {
            text = `<strong> ${text} </strong>`;
          }
          if (child.italic) {
            text = `<em> ${text} </em>`;
          }

          return text;
        })
        .join("");

      switch (node.type) {
        case "paragraph":
          return `<p>${children}</p>`;
        case "heading-one":
          return `<h1>${children}</h1>`;
        case "heading-two":
          return `<h2>${children}</h2>`;
        case "heading-three":
          return `<h3>${children}</h3>`;
        case "heading-four":
          return `<h4>${children}</h4>`;
        case "heading-five":
          return `<h5>${children}</h5>`;
        case "heading-six":
          return `<h6>${children}</h6>`;
        case "list-item":
          return `<li>${children}</li>`;
        default:
          return children;
      }
    })
    .join("");
};

const toggleMark = (
  editor: BaseEditor & ReactEditor & HistoryEditor,
  format: keyof Omit<CustomText, "text">,
) => {
  const isActive = isMarkActive(editor, format);

  if (isActive) {
    Editor.removeMark(editor, format);
  } else {
    Editor.addMark(editor, format, true);
  }
};

const isMarkActive = (
  editor: BaseEditor & ReactEditor & HistoryEditor,
  format: keyof Omit<CustomText, "text">,
) => {
  const marks = Editor.marks(editor);
  return marks ? marks[format] === true : false;
};

// Add function to toggle block type
const toggleBlock = (
  editor: BaseEditor & ReactEditor & HistoryEditor,
  format: string,
) => {
  const isActive = isBlockActive(editor, format);
  const isList = format === "bulleted-list";

  if (isList) {
    wrapListItem(editor);
    return;
  }

  Transforms.setNodes(
    editor,
    { type: isActive ? "paragraph" : format } as Partial<SlateElement>,
    { match: (n) => !Editor.isEditor(n) && SlateElement.isElement(n) },
  );
};

interface ToolbarButtonProps {
  format:
    | keyof Omit<CustomText, "text">
    | "heading-one"
    | "heading-two"
    | "bulleted-list"
    | "list-item";
  icon: React.ReactNode;
  isBlock?: boolean;
}

const ToolbarButton = ({
  format,
  icon,
  isBlock = false,
}: ToolbarButtonProps) => {
  const editor = useSlate();

  const handleClick = (event: React.MouseEvent) => {
    event.preventDefault();
    if (isBlock) {
      toggleBlock(editor, format);
    } else {
      toggleMark(editor, format as keyof Omit<CustomText, "text">);
    }
  };

  const isActive = isBlock
    ? isBlockActive(editor, format)
    : isMarkActive(editor, format as keyof Omit<CustomText, "text">);

  return (
    <button
      onMouseDown={handleClick}
      className={`p-1 mx-1 text-sm rounded ${
        isActive
          ? "bg-gray-200 text-gray-900"
          : "text-gray-600 hover:bg-gray-100"
      }`}
    >
      {icon}
    </button>
  );
};

// Fix the wrapListItem function to properly handle unwrapping and fix type issues
const wrapListItem = (editor: BaseEditor & ReactEditor & HistoryEditor) => {
  const isActive = isBlockActive(editor, "list-item");

  if (isActive) {
    // First change the list items back to paragraphs
    Transforms.setNodes(
      editor,
      { type: "paragraph" } as Partial<SlateElement>,
      {
        match: (n) =>
          !Editor.isEditor(n) &&
          SlateElement.isElement(n) &&
          (n as CustomElement).type === "list-item",
      },
    );

    // Then unwrap them from the bulleted list parent
    Transforms.unwrapNodes(editor, {
      match: (n) =>
        !Editor.isEditor(n) &&
        SlateElement.isElement(n) &&
        (n as CustomElement).type === "bulleted-list",
      split: true,
    });
  } else {
    const isBulletedListActive = isBlockActive(editor, "bulleted-list");

    if (!isBulletedListActive) {
      // Create an empty bulleted-list element with proper typing
      const bulletedList: CustomElement = {
        type: "bulleted-list",
        children: [],
      };

      Transforms.wrapNodes(editor, bulletedList, { split: true });
    }

    Transforms.setNodes(
      editor,
      { type: "list-item" } as Partial<SlateElement>,
      { split: true },
    );
  }
};

const isBlockActive = (
  editor: BaseEditor & ReactEditor & HistoryEditor,
  format: string,
) => {
  const { selection } = editor;
  if (!selection) return false;

  const [match] = Array.from(
    Editor.nodes(editor, {
      at: Editor.unhangRange(editor, selection),
      match: (n) =>
        !Editor.isEditor(n) &&
        SlateElement.isElement(n) &&
        (n as CustomElement).type === format,
    }),
  );

  return !!match;
};

// Add a button in the toolbar for list-item
const Toolbar = () => {
  return (
    <div className="flex items-center p-1 border-b border-gray-200">
      <ToolbarButton format="bold" icon={<Bold size={14} />} />
      <ToolbarButton format="italic" icon={<Italic size={14} />} />
      <ToolbarButton format="heading-one" icon={<span>H1</span>} isBlock />
      <ToolbarButton format="heading-two" icon={<span>H2</span>} isBlock />
      <ToolbarButton format="bulleted-list" icon={<span>UL</span>} isBlock />
    </div>
  );
};

const RichHTMLTextEditor = ({
  value,
  onChange,
}: {
  value: string;
  onChange: (value: string) => void;
}) => {
  const editor = useMemo(() => withHistory(withReact(createEditor())), []);

  const renderElement = useCallback(
    (props: RenderElementProps) => <Element {...props} />,
    [],
  );
  const renderLeaf = useCallback(
    (props: RenderLeafProps) => <Leaf {...props} />,
    [],
  );

  const initialEditorValue = useMemo(() => {
    if (typeof value === "string") {
      return deserialize(value);
    }
    return value;
  }, [value]);

  return (
    <div className="border rounded-md">
      <Slate
        editor={editor}
        initialValue={initialEditorValue as Descendant[]}
        onChange={(newValue) => {
          // Convert Slate format back to markdown string when saving
          const markdownValue = serialize(newValue as CustomElement[]);
          onChange(markdownValue);
        }}
      >
        <Toolbar />
        <div className="p-2">
          <Editable
            renderElement={renderElement}
            renderLeaf={renderLeaf}
            placeholder="Enter description..."
            spellCheck
            autoFocus
            style={{ outline: "none" }}
          />
        </div>
      </Slate>
    </div>
  );
};

const Element = ({ attributes, children, element }: RenderElementProps) => {
  switch (element.type) {
    case "heading-one":
      return (
        <TextComponent variant="h1" {...attributes} className="my-3">
          {children}
        </TextComponent>
      );
    case "heading-two":
      return (
        <TextComponent variant="h2" {...attributes} className="my-3">
          {children}
        </TextComponent>
      );
    case "heading-three":
      return (
        <TextComponent variant="h3" {...attributes} className="my-3">
          {children}
        </TextComponent>
      );
    case "heading-four":
      return (
        <TextComponent variant="h4" {...attributes} className="my-3">
          {children}
        </TextComponent>
      );
    case "heading-five":
      return (
        <TextComponent variant="h5" {...attributes} className="my-3">
          {children}
        </TextComponent>
      );
    case "heading-six":
      return (
        <TextComponent variant="h6" {...attributes} className="my-3">
          {children}
        </TextComponent>
      );
    case "list-item":
      return (
        <li className="list-item ml-6 pb-3" {...attributes}>
          {children}
        </li>
      );
    case "bulleted-list":
      return (
        <ul className="list-disc pl-0 space-y-1" {...attributes}>
          {children}
        </ul>
      );
    default:
      return (
        <TextComponent variant="p" {...attributes}>
          {children}
        </TextComponent>
      );
  }
};

const Leaf = ({ attributes, children, leaf }: RenderLeafProps) => {
  if (leaf.bold) {
    children = <strong>{children}</strong>;
  }

  if (leaf.italic) {
    children = <em>{children}</em>;
  }

  return <span {...attributes}>{children}</span>;
};

export default RichHTMLTextEditor;
